import { Minus, Package, Plus, Scissors, Trash2, Undo2 } from 'lucide-react'

import type { PosOrderItem } from './pos-types'
import { useAuth } from '@/hooks/use-auth'
import { formatVND } from '@/lib/format'
import { canSplitMaterial, showSplitToggle } from '@/lib/material-split'
import { capturePosCartQuantityStep } from '@/lib/pos-cart-quantity-tracking'
import { getProductImageURL } from '@/lib/product-image'
import { m } from '@/lib/paraglide/messages'

interface Props {
    item: PosOrderItem
    index: number
    onUpdate: (
        index: number,
        field: 'quantity' | 'unit_price' | 'discount_percent',
        value: number,
    ) => void
    /** Toggle tách/gộp giá vật tư dòng — parent reset đơn giá từ master + đổi cờ. */
    onToggleSplit?: (index: number) => void
    onRemove: (index: number) => void
}

function safeNumber(val: string, fallback: number, min: number) {
    const n = Number(val)
    return Number.isFinite(n) ? Math.max(min, n) : fallback
}

// Ngưỡng thấp nhất cho phép: bất kỳ số dương nào (BE validate gt=0). Dùng
// 1e-15 tránh 0 exact do float-parse, không giới hạn precision người nhập.
const POS_QTY_MIN = 1e-15

// Thu ngân bôi đen ô SL rồi xoá để gõ số mới: ô rỗng phải Ở LẠI rỗng. Ép về
// POS_QTY_MIN làm ô hiện "1e-15" và không gõ tiếp được (lỗi thật trên quầy
// 09/09/2026). 0 là trạng thái "đang gõ" — Bán ngay vẫn bấm được và lỗi thiếu
// số lượng hiện ở bước validate, không disable nút.
function quantityFromInput(raw: string, fallback: number) {
    if (raw.trim() === '') return 0
    const n = Number(raw)
    if (!Number.isFinite(n)) return fallback
    return n <= 0 ? 0 : Math.max(POS_QTY_MIN, n)
}

// Nút "−" giảm 1, thấp nhất 1; SL ≤ 1 thì nút tắt (xoá dòng dùng thùng rác).
// Làm tròn theo số chữ số thập phân của SL hiện tại để trừ 1 không sinh đuôi
// float (2.3 - 1 = 1.2999999999999998). SL > 1 nên String() không ra dạng mũ.
function decreasedQuantity(qty: number) {
    const decimals = String(qty).split('.')[1]?.length ?? 0
    return Math.max(1, Number((qty - 1).toFixed(decimals)))
}

export function PosOrderItemRow({
    item,
    index,
    onUpdate,
    onToggleSplit,
    onRemove,
}: Props) {
    const { hasPermission } = useAuth()
    const canEditDiscount = hasPermission('sales_orders.edit_discount')
    const lineTotal =
        item.quantity * item.unit_price * (1 - item.discount_percent / 100)

    const materialPricePerUnit = item.material_price_per_unit ?? 0
    const masterUnitPrice = item.master_unit_price ?? item.unit_price
    const split = item.material_price_split ?? false
    const canToggle =
        !!onToggleSplit &&
        showSplitToggle(item.price_includes_material, materialPricePerUnit)
    const splitAllowed = canSplitMaterial(masterUnitPrice, materialPricePerUnit)

    return (
        <div className="flex flex-wrap items-center gap-2 px-3 py-2 border-b bg-card text-sm">
            {/* Row number */}
            <span className="w-5 text-muted-foreground text-xs shrink-0">
                {index + 1}
            </span>

            {/* Delete */}
            <button
                type="button"
                onClick={() => onRemove(index)}
                className="text-muted-foreground hover:text-destructive shrink-0"
            >
                <Trash2 className="size-3.5" />
            </button>

            {/* Ảnh SP — thu ngân đối chiếu nhanh với hàng khách đưa. */}
            <div className="size-9 rounded bg-muted flex items-center justify-center shrink-0 overflow-hidden">
                {item.image_url ? (
                    <img
                        src={getProductImageURL(item.image_url)}
                        alt={item.product_name}
                        className="size-full object-cover"
                        loading="lazy"
                        // Ảnh lỗi/thiếu file thì ẩn hẳn, để lại ô xám thay vì
                        // icon ảnh vỡ.
                        onError={(e) => {
                            e.currentTarget.style.display = 'none'
                        }}
                    />
                ) : (
                    <Package className="size-4 text-muted-foreground" />
                )}
            </div>

            {/* Product info */}
            <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{item.product_name}</div>
                <div className="text-xs text-muted-foreground">{item.sku}</div>
            </div>

            {/* Quantity */}
            <div className="flex items-center gap-1 shrink-0">
                <button
                    type="button"
                    aria-label={m.pos_order_item_quantity_decrease()}
                    title={m.pos_order_item_quantity_decrease()}
                    disabled={!(item.quantity > 1)}
                    onClick={() => {
                        const next = decreasedQuantity(item.quantity)
                        onUpdate(index, 'quantity', next)
                        capturePosCartQuantityStep({
                            direction: 'decrease',
                            reachedMinimum: next === 1,
                        })
                    }}
                    className="p-0.5 rounded hover:bg-muted text-muted-foreground disabled:opacity-40 disabled:pointer-events-none"
                >
                    <Minus className="size-3.5" />
                </button>
                <input
                    type="number"
                    min={POS_QTY_MIN}
                    step="any"
                    inputMode="decimal"
                    value={item.quantity === 0 ? '' : item.quantity}
                    onChange={(e) =>
                        onUpdate(
                            index,
                            'quantity',
                            quantityFromInput(e.target.value, item.quantity),
                        )
                    }
                    className="w-14 text-center border-b border-dashed border-primary/50 bg-transparent text-sm focus:outline-none focus:border-primary"
                />
                {item.unit && (
                    <span className="text-xs text-muted-foreground">
                        {item.unit}
                    </span>
                )}
                <button
                    type="button"
                    onClick={() => {
                        onUpdate(index, 'quantity', item.quantity + 1)
                        capturePosCartQuantityStep({
                            direction: 'increase',
                            reachedMinimum: false,
                        })
                    }}
                    className="p-0.5 rounded hover:bg-muted text-muted-foreground"
                >
                    <Plus className="size-3.5" />
                </button>
            </div>

            {/* Unit price */}
            <input
                type="number"
                min={0}
                step="any"
                inputMode="decimal"
                value={item.unit_price}
                onChange={(e) =>
                    onUpdate(
                        index,
                        'unit_price',
                        safeNumber(e.target.value, item.unit_price, 0),
                    )
                }
                className="w-20 text-right border-b border-dashed border-primary/50 bg-transparent text-sm focus:outline-none focus:border-primary shrink-0"
            />

            {/* Discount percent */}
            <div className="flex items-center gap-0.5 shrink-0">
                {canEditDiscount ? (
                    <input
                        type="number"
                        min={0}
                        max={100}
                        value={item.discount_percent}
                        onChange={(e) =>
                            onUpdate(
                                index,
                                'discount_percent',
                                safeNumber(
                                    e.target.value,
                                    item.discount_percent,
                                    0,
                                ),
                            )
                        }
                        className="w-10 text-center border-b border-dashed border-primary/50 bg-transparent text-sm focus:outline-none focus:border-primary"
                    />
                ) : (
                    <span className="w-10 text-center text-sm text-muted-foreground">
                        {item.discount_percent}
                    </span>
                )}
                <span className="text-xs text-muted-foreground">%</span>
            </div>

            {/* Line total */}
            <span className="w-24 text-right font-semibold shrink-0">
                {formatVND(lineTotal)}
            </span>

            {/* Tách/Gộp giá vật tư — chỉ SP price_includes_material. Wrap xuống
                dòng riêng (basis-full) cho gọn trong cart hẹp. */}
            {canToggle ? (
                <div className="basis-full flex items-center gap-2 pl-7">
                    <button
                        type="button"
                        disabled={!split && !splitAllowed}
                        onClick={() => onToggleSplit?.(index)}
                        className={`inline-flex items-center gap-1 rounded border px-2 py-0.5 text-xs disabled:opacity-50 ${
                            split
                                ? 'border-primary/40 bg-primary/10 text-primary'
                                : 'border-input text-muted-foreground hover:bg-muted'
                        }`}
                    >
                        {split ? (
                            <Undo2 className="size-3" />
                        ) : (
                            <Scissors className="size-3" />
                        )}
                        {split
                            ? m.order_material_merge_button()
                            : m.order_material_split_button()}
                    </button>
                    {!split && !splitAllowed ? (
                        <span className="text-[11px] text-amber-600">
                            {m.order_material_split_negative_hint()}
                        </span>
                    ) : null}
                </div>
            ) : null}
        </div>
    )
}
